import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  EyeIcon, 
  CodeBracketIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import templateService, { EmailTemplate, CreateTemplateData, TemplateVariable, ValidationResult } from '../../services/templateService';

interface TemplateEditorProps {
  template?: EmailTemplate; // If provided, we're editing; otherwise creating
  onSave: (template: EmailTemplate) => void;
  onCancel: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onSave, onCancel }) => {
  const [formData, setFormData] = useState<CreateTemplateData>({
    name: template?.name || '',
    description: template?.description || '',
    category: template?.category || 'newsletter',
    subject_template: template?.subject_template || '',
    content_html_template: template?.content_html_template || '',
    content_text_template: template?.content_text_template || '',
    variables_schema: template?.variables_schema || [],
    segment_tags: template?.segment_tags || [],
    personalization_level: template?.personalization_level || 'medium'
  });

  const [previewMode, setPreviewMode] = useState<'editor' | 'preview'>('editor');
  const [activeTab, setActiveTab] = useState<'content' | 'variables' | 'settings'>('content');
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extractedVars, setExtractedVars] = useState<string[]>([]);

  const isEditing = !!template;

  // Extract variables when content changes
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (formData.subject_template || formData.content_html_template) {
        try {
          const variables = await templateService.extractVariables({
            subject_template: formData.subject_template,
            content_html_template: formData.content_html_template,
            content_text_template: formData.content_text_template
          });
          setExtractedVars(variables);
        } catch (err) {
          console.warn('Failed to extract variables:', err);
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.subject_template, formData.content_html_template, formData.content_text_template]);

  const handleInputChange = (field: keyof CreateTemplateData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setValidation(null); // Clear validation when content changes
  };

  const handleVariableAdd = () => {
    const newVariable: TemplateVariable = {
      key: '',
      label: '',
      type: 'text',
      required: false,
      description: ''
    };

    handleInputChange('variables_schema', [...(formData.variables_schema || []), newVariable]);
  };

  const handleVariableUpdate = (index: number, variable: TemplateVariable) => {
    const updatedVariables = [...(formData.variables_schema || [])];
    updatedVariables[index] = variable;
    handleInputChange('variables_schema', updatedVariables);
  };

  const handleVariableDelete = (index: number) => {
    const updatedVariables = [...(formData.variables_schema || [])];
    updatedVariables.splice(index, 1);
    handleInputChange('variables_schema', updatedVariables);
  };

  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !formData.segment_tags?.includes(tag.trim())) {
      handleInputChange('segment_tags', [...(formData.segment_tags || []), tag.trim()]);
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    handleInputChange('segment_tags', formData.segment_tags?.filter(tag => tag !== tagToRemove) || []);
  };

  const validateTemplate = async () => {
    try {
      const result = await templateService.validateTemplate(formData);
      setValidation(result);
      return result;
    } catch (err) {
      setValidation({
        isValid: false,
        errors: [err instanceof Error ? err.message : 'Validation failed'],
        warnings: []
      });
      return null;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate first
      const validationResult = await validateTemplate();
      if (!validationResult?.isValid) {
        setError('Please fix validation errors before saving');
        return;
      }

      let savedTemplate: EmailTemplate;

      if (isEditing) {
        savedTemplate = await templateService.updateTemplate(template.id, formData);
      } else {
        const result = await templateService.createTemplate(formData);
        savedTemplate = result.template;
      }

      onSave(savedTemplate);
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const generateTextFromHtml = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const handleHtmlContentChange = (html: string) => {
    handleInputChange('content_html_template', html);
    // Auto-generate text version if it's empty
    if (!formData.content_text_template) {
      handleInputChange('content_text_template', generateTextFromHtml(html));
    }
  };

  const categories = [
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'resources', label: 'Resources' },
    { value: 'events', label: 'Events' },
    { value: 'general', label: 'General' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {isEditing ? 'Edit Template' : 'Create New Template'}
            </h3>
            <p className="text-sm text-gray-600">
              {isEditing ? `Editing "${template.name}"` : 'Create a reusable email template with consistent branding'}
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-[calc(95vh-140px)]">
          {/* Left Panel - Form */}
          <div className="flex-1 lg:w-1/2 overflow-y-auto border-r border-gray-200">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 px-6">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'content', label: 'Content', icon: CodeBracketIcon },
                  { id: 'variables', label: 'Variables', icon: PlusIcon },
                  { id: 'settings', label: 'Settings', icon: CheckCircleIcon }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Validation Display */}
              {validation && (
                <div className="mb-6">
                  {!validation.isValid && validation.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                        <div>
                          <h4 className="text-red-800 font-medium">Validation Errors</h4>
                          <ul className="text-red-700 text-sm mt-1 list-disc list-inside">
                            {validation.errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {validation.warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                        <div>
                          <h4 className="text-yellow-800 font-medium">Warnings</h4>
                          <ul className="text-yellow-700 text-sm mt-1 list-disc list-inside">
                            {validation.warnings.map((warning, index) => (
                              <li key={index}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="space-y-6">
                  {/* Subject Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Line Template *
                    </label>
                    <input
                      type="text"
                      value={formData.subject_template}
                      onChange={(e) => handleInputChange('subject_template', e.target.value)}
                      placeholder="{{school_name}} Newsletter - {{newsletter_title}}"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use {`{{variable_name}}`} for dynamic content
                    </p>
                  </div>

                  {/* HTML Content Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML Content Template *
                    </label>
                    <textarea
                      value={formData.content_html_template}
                      onChange={(e) => handleHtmlContentChange(e.target.value)}
                      rows={20}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm"
                      placeholder="<!DOCTYPE html>&#10;<html>&#10;<head>&#10;    <title>{{newsletter_title}}</title>&#10;</head>&#10;<body>&#10;    <div style='max-width: 600px; margin: 0 auto;'>&#10;        <h1>{{school_name}}</h1>&#10;        <p>Hello {{parent_name}},</p>&#10;        <div>{{main_content}}</div>&#10;    </div>&#10;</body>&#10;</html>"
                    />
                  </div>

                  {/* Text Content Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plain Text Template
                    </label>
                    <textarea
                      value={formData.content_text_template}
                      onChange={(e) => handleInputChange('content_text_template', e.target.value)}
                      rows={10}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm"
                      placeholder="Plain text version of your email..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-generated from HTML if left empty
                    </p>
                  </div>

                  {/* Extracted Variables */}
                  {extractedVars.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <h4 className="text-blue-800 font-medium mb-2">Detected Variables</h4>
                      <div className="flex flex-wrap gap-2">
                        {extractedVars.map((variable, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded font-mono"
                          >
                            {`{{${variable}}}`}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        Define these in the Variables tab for better AI generation
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Variables Tab */}
              {activeTab === 'variables' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">Template Variables</h4>
                    <button
                      onClick={handleVariableAdd}
                      className="bg-indigo-600 text-white px-3 py-2 rounded-md text-sm hover:bg-indigo-700 flex items-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Variable
                    </button>
                  </div>

                  <p className="text-sm text-gray-600">
                    Define variables that can be used in your template. This helps AI understand what content to generate.
                  </p>

                  <div className="space-y-4">
                    {formData.variables_schema?.map((variable, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-md">
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Variable Key *
                            </label>
                            <input
                              type="text"
                              value={variable.key}
                              onChange={(e) => handleVariableUpdate(index, { ...variable, key: e.target.value })}
                              placeholder="main_content"
                              className="w-full text-sm border-gray-300 rounded focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Display Label *
                            </label>
                            <input
                              type="text"
                              value={variable.label}
                              onChange={(e) => handleVariableUpdate(index, { ...variable, label: e.target.value })}
                              placeholder="Main Content"
                              className="w-full text-sm border-gray-300 rounded focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={variable.type}
                              onChange={(e) => handleVariableUpdate(index, { ...variable, type: e.target.value as any })}
                              className="w-full text-sm border-gray-300 rounded focus:border-indigo-500 focus:ring-indigo-500"
                            >
                              <option value="text">Text</option>
                              <option value="html">HTML</option>
                              <option value="url">URL</option>
                              <option value="image">Image</option>
                            </select>
                          </div>
                          <div className="flex items-center mt-6">
                            <input
                              type="checkbox"
                              checked={variable.required}
                              onChange={(e) => handleVariableUpdate(index, { ...variable, required: e.target.checked })}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-xs text-gray-700">Required</label>
                          </div>
                          <div className="flex justify-end mt-6">
                            <button
                              onClick={() => handleVariableDelete(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Default Value
                            </label>
                            <input
                              type="text"
                              value={variable.default_value || ''}
                              onChange={(e) => handleVariableUpdate(index, { ...variable, default_value: e.target.value })}
                              placeholder="Optional default value"
                              className="w-full text-sm border-gray-300 rounded focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <input
                              type="text"
                              value={variable.description || ''}
                              onChange={(e) => handleVariableUpdate(index, { ...variable, description: e.target.value })}
                              placeholder="What this variable is for"
                              className="w-full text-sm border-gray-300 rounded focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {(!formData.variables_schema || formData.variables_schema.length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        <PlusIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No variables defined yet</p>
                        <p className="text-sm">Add variables to help AI understand your template structure</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  {/* Basic Settings */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Template Settings</h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Template Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Professional Newsletter Template"
                          className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category *
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Personalization Level
                      </label>
                      <select
                        value={formData.personalization_level}
                        onChange={(e) => handleInputChange('personalization_level', e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="light">Light - Names only</option>
                        <option value="medium">Medium - Names + Age</option>
                        <option value="heavy">Heavy - Names + Age + Specific Needs</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Controls how much personal information AI uses when generating campaigns from this template
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={3}
                        placeholder="Brief description of when to use this template..."
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Segment Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Segment Tags
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Add tags to help the AI choose the right template for different segments
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.segment_tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm flex items-center"
                        >
                          {tag}
                          <button
                            onClick={() => handleTagRemove(tag)}
                            className="ml-1 text-indigo-600 hover:text-indigo-800"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>

                    <div className="flex space-x-2">
                      <input
                        type="text"
                        placeholder="Add tag..."
                        className="flex-1 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleTagAdd((e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const input = document.querySelector('input[placeholder="Add tag..."]') as HTMLInputElement;
                          if (input.value) {
                            handleTagAdd(input.value);
                            input.value = '';
                          }
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={validateTemplate}
                      className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 mb-4"
                    >
                      Validate Template
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 lg:w-1/2 bg-gray-50 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 p-6 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">Template Preview</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPreviewMode('editor')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      previewMode === 'editor'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    <CodeBracketIcon className="h-4 w-4 inline mr-1" />
                    Source
                  </button>
                  <button
                    onClick={() => setPreviewMode('preview')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      previewMode === 'preview'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    <EyeIcon className="h-4 w-4 inline mr-1" />
                    Preview
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {formData.subject_template || formData.content_html_template ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Subject Preview */}
                  <div className="border-b border-gray-200 p-4">
                    <div className="text-sm text-gray-500 mb-1">Subject:</div>
                    <div className="text-lg font-medium text-gray-900">
                      {formData.subject_template || 'No subject template defined'}
                    </div>
                  </div>

                  {/* Content Preview */}
                  <div className="p-4">
                    {previewMode === 'preview' ? (
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: formData.content_html_template
                            .replace(/\{\{(\w+)\}\}/g, '<span style="background-color: #fef3c7; padding: 2px 4px; border-radius: 4px; font-weight: 600;">{{$1}}</span>')
                        }}
                      />
                    ) : (
                      <pre className="bg-gray-900 text-gray-300 p-4 rounded-md text-sm overflow-x-auto whitespace-pre-wrap">
                        {formData.content_html_template || 'No HTML content defined'}
                      </pre>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <div className="text-gray-400 text-lg mb-2">📧</div>
                  <div className="text-gray-500 text-sm">
                    Start adding content to see the preview
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {extractedVars.length > 0 && (
              <span>{extractedVars.length} variables detected</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Template' : 'Create Template'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;